import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { NotificationSource } from "../../../models/transport/transportOrderNotification";
import { getTransportOrderNotifications, confirmTransportOrderNotification } from "../../../services/transportOrderNotificationService";
import { getNotifications, confirmNotification } from "../../../services/notificationsService";
import { AppNotification, GenericNotificationDto, isGenericNotification } from "../../../models/notification/notification";

export interface NotificationState {
    unconfirmedCount: number;
    lastUnconfirmedCount: number;
    notifications: AppNotification[];
    loading: boolean;
    genericLoading: boolean;
}

const initialState: NotificationState = {
    unconfirmedCount: 0,
    lastUnconfirmedCount: 0,
    notifications: [],
    loading: false,
    genericLoading: false,
};

export const fetchTransportNotifications = createAsyncThunk(
    "notification/fetchTransportNotifications",
    async(employeeId: string, { rejectWithValue }) => {
        try {
            const response = await getTransportOrderNotifications({
                employeeId,
                source: NotificationSource.Desktop
            });
            return response.data.map(n => ({ ...n, type: "transportOrder" }));
        } catch (error) {
            console.error("Error fetching transport notifications:", error);
            return rejectWithValue(error);
        }
    }
);

export const fetchGenericNotifications = createAsyncThunk(
    "notification/fetchGenericNotifications",
    async(_: void, { rejectWithValue }) => {
        try {
            const response = await getNotifications({ onlyUnconfirmed: true });
            const mapped = response.data.reduce((arr: GenericNotificationDto[], g: any) => {
                const id = g.id ?? g.Id ?? g.notificationId;
                if (id) {
                    const mappedItem: GenericNotificationDto = {
                        id: String(id),
                        type: String(g.type ?? ""),
                        messageKey: String(g.messageKey ?? g.MessageKey ?? ""),
                        titleKey: g.titleKey ?? g.TitleKey,
                        contextJson: g.contextJson ?? g.ContextJson,
                        createdDateTime: String(g.createdDateTime ?? g.CreatedDateTime ?? ""),
                        confirmedDateTime: g.confirmedDateTime ?? g.ConfirmedDateTime ?? null,
                    };
                    if (!mappedItem.confirmedDateTime) {
                        arr.push(mappedItem);
                    }
                }
                return arr;
            }, [] as GenericNotificationDto[]);
            return mapped;
        } catch (error) {
            console.error("Error fetching generic notifications:", error);
            return rejectWithValue(error);
        }
    }
);

export const confirmTransportNotification = createAsyncThunk(
    "notification/confirmTransportNotification",
    async(transportOrderNotificationId: string, { rejectWithValue }) => {
        try {
            await confirmTransportOrderNotification(transportOrderNotificationId);
            return transportOrderNotificationId;
        } catch (error) {
            console.error("Error confirming transport notification:", error);
            return rejectWithValue(error);
        }
    }
);

export const confirmGenericNotificationThunk = createAsyncThunk(
    "notification/confirmGenericNotification",
    async(id: string, { rejectWithValue }) => {
        try {
            const response = await confirmNotification(id);
            const res = response.data as any;
            return (res?.id ?? res?.Id ?? res?.notificationId ?? id) as string;
        } catch (error) {
            console.error("Error confirming generic notification:", error);
            return rejectWithValue(error);
        }
    }
);

export const notificationSlice = createSlice({
    name: "notification",
    initialState,
    reducers: {
        setNotifications(state, action: PayloadAction<AppNotification[]>) {
            state.notifications = action.payload;
            state.unconfirmedCount = action.payload.length;
        },
        clearNotifications(state) {
            state.notifications = [];
            state.unconfirmedCount = 0;
            state.lastUnconfirmedCount = 0;
        },
        setLastUnconfirmedCount(state, action: PayloadAction<number>) {
            state.lastUnconfirmedCount = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTransportNotifications.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchTransportNotifications.fulfilled, (state, action: PayloadAction<AppNotification[]>) => {
                const generic = state.notifications.filter(n => isGenericNotification(n));
                state.notifications = [...generic, ...action.payload];
                state.loading = false;
                state.unconfirmedCount = state.notifications.length;
            })
            .addCase(fetchTransportNotifications.rejected, (state) => {
                state.loading = false;
            })
            .addCase(fetchGenericNotifications.pending, (state) => {
                state.genericLoading = true;
            })
            .addCase(fetchGenericNotifications.fulfilled, (state, action: PayloadAction<GenericNotificationDto[]>) => {
                const transports = state.notifications.filter(n => !isGenericNotification(n));
                state.notifications = [...action.payload, ...transports];
                state.genericLoading = false;
                state.unconfirmedCount = state.notifications.length;
            })
            .addCase(fetchGenericNotifications.rejected, (state) => {
                state.genericLoading = false;
            })
            .addCase(confirmTransportNotification.fulfilled, (state, action: PayloadAction<string>) => {
                state.notifications = state.notifications.filter(n => n.id !== action.payload);
                state.unconfirmedCount = state.notifications.length;
            })
            .addCase(confirmGenericNotificationThunk.fulfilled, (state, action: PayloadAction<string>) => {
                state.notifications = state.notifications.filter(n => n.id !== action.payload);
                state.unconfirmedCount = state.notifications.length;
            });
    },
});

export const notificationReducer = notificationSlice.reducer;

export const { setNotifications, clearNotifications, setLastUnconfirmedCount } = notificationSlice.actions;
